import React from 'react';

/**
 * CSRF Protection Utilities
 *
 * This module provides client-side CSRF protection for form submissions.
 * It generates and validates CSRF tokens to prevent cross-site request forgery attacks.
 */

const CSRF_TOKEN_KEY = 'csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * Generate a cryptographically secure CSRF token
 */
export function generateCSRFToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Store CSRF token in session storage
 */
export function storeCSRFToken(token: string): void {
  sessionStorage.setItem(CSRF_TOKEN_KEY, token);
}

/**
 * Retrieve CSRF token from session storage
 */
export function getCSRFToken(): string | null {
  return sessionStorage.getItem(CSRF_TOKEN_KEY);
}

/**
 * Remove CSRF token from session storage
 */
export function clearCSRFToken(): void {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
}

/**
 * Initialize CSRF token for the current session
 */
export function initializeCSRFToken(): string {
  let token = getCSRFToken();

  if (!token) {
    token = generateCSRFToken();
    storeCSRFToken(token);
  }

  return token;
}

/**
 * Get headers with CSRF token for API requests
 */
export function getCSRFHeaders(): Record<string, string> {
  const token = getCSRFToken();

  if (!token) {
    if (import.meta.env.DEV) {
      console.warn('CSRF token not found. Initializing new token.');
    }
    const newToken = initializeCSRFToken();
    return { [CSRF_HEADER_NAME]: newToken };
  }

  return { [CSRF_HEADER_NAME]: token };
}

/**
 * Custom hook for CSRF protection in React components
 */
export function useCSRFProtection() {
  const [csrfToken, setCSRFToken] = React.useState<string>('');

  React.useEffect(() => {
    const token = initializeCSRFToken();
    setCSRFToken(token);
  }, []);

  const refreshToken = React.useCallback(() => {
    const newToken = generateCSRFToken();
    storeCSRFToken(newToken);
    setCSRFToken(newToken);
    return newToken;
  }, []);

  const getHeaders = React.useCallback(() => {
    return getCSRFHeaders();
  }, []);

  return {
    csrfToken,
    refreshToken,
    getHeaders,
  };
}
